// Langue de l'interface : français (langue d'écriture du code) ou anglais.
//
// Le texte français sert de clé : t("Entrées") affiche « Entrées » en français, EN["Entrées"] en anglais.
// - Variables : t("{n} alarmes", { n }) ; balises : rich("Un <b>lien</b>", { b: (s) => <b>{s}</b> }).
// - Même mot, deux sens : tc("contexte", "texte") (clé anglaise « contexte|texte »).
// - Une étiquette rangée dans une table (états, rôles...) est marquée N_("...") et affichée avec t(table[clé]).
// - Toujours une chaîne entre guillemets doubles, jamais une variable ni un `gabarit` : le test i18n.test.ts lit les
//   sources et vérifie que chaque texte a sa traduction (et qu'aucune traduction n'est orpheline).
//
// Choix : celui du compte (enregistré sur le serveur), sinon celui du navigateur (gw_lang), sinon sa langue.
import { createElement, Fragment, useSyncExternalStore, type ReactNode } from "react";
import { EN } from "../i18n/en";

export type Lang = "fr" | "en";
export const LANGS: { value: Lang; label: string }[] = [
  { value: "fr", label: "Français" },
  { value: "en", label: "English" },
];
const STORAGE_KEY = "gw_lang";

function browserLang(): Lang {
  // lien d'un e-mail (invitation, confirmation, accès) : ?lang=en ouvre la page dans la langue du message. Lu une
  // fois puis retiré de l'adresse : sinon un rechargement imposerait cette langue après un autre choix.
  const params = typeof location !== "undefined" ? new URLSearchParams(location.search) : null;
  const asked = params?.get("lang");
  if (asked === "fr" || asked === "en") {
    try {
      localStorage.setItem(STORAGE_KEY, asked);
    } catch {
      // stockage indisponible : la langue vaut pour cette visite
    }
    params!.delete("lang");
    const rest = params!.toString();
    history.replaceState(null, "", `${location.pathname}${rest ? `?${rest}` : ""}${location.hash}`);
    return asked;
  }
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "fr" || saved === "en") return saved;
  } catch {
    // stockage indisponible (navigation privée stricte) : langue du navigateur
  }
  const nav = typeof navigator !== "undefined" ? navigator.language : "fr";
  return nav.toLowerCase().startsWith("fr") ? "fr" : "en";
}

let current: Lang = browserLang();
const listeners = new Set<() => void>();
if (typeof document !== "undefined") document.documentElement.lang = current;

export function getLang(): Lang {
  return current;
}

// Langue choisie sur une page publique (connexion, invitation...) : à la connexion, elle l'emporte sur celle du
// compte et y est enregistrée (sinon le compte imposerait l'ancienne langue juste après le choix).
let publicChoice: Lang | null = null;

export function choosePublicLang(lang: Lang): void {
  publicChoice = lang;
  setLang(lang);
}

/** Choix fait avant la connexion, s'il y en a un (une seule fois). */
export function takePublicChoice(): Lang | null {
  const chosen = publicChoice;
  publicChoice = null;
  return chosen;
}

/** Change la langue affichée (et la garde pour ce navigateur). N'enregistre rien sur le compte. */
export function setLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    // rien : la langue vaut pour cette visite
  }
  if (lang === current) return;
  current = lang;
  if (typeof document !== "undefined") document.documentElement.lang = lang;
  listeners.forEach((fn) => fn());
}

/** Langue courante ; le composant est redessiné quand elle change (ou quand les messages du serveur arrivent). */
export function useLang(): Lang {
  useSyncExternalStore(subscribe, snapshot);
  return current;
}

function snapshot(): string {
  return `${current}:${serverVersion}`;
}

// ---------------------------------------------------------------- messages du serveur
// Journal, alarmes, erreurs des sorties : écrits en français par le serveur, traduits ici à l'affichage avec son
// catalogue (GET /api/i18n/en, même règle que backend/app/i18n.py : {nom} = partie variable, traduite elle aussi
// si elle est reconnue).
let serverExact: Record<string, string> = {};
let serverPatterns: { regex: RegExp; en: string }[] = [];
let serverVersion = 0;
// déjà traduits : le journal (jusqu'à 500 lignes) est redessiné à chaque mise à jour en direct
const serverCache = new Map<string, string>();
const SERVER_CACHE_MAX = 5000;

export function setServerMessages(catalog: Record<string, string>): void {
  serverExact = catalog;
  serverPatterns = Object.entries(catalog)
    .filter(([fr]) => fr.includes("{"))
    .map(([fr, en]) => {
      const parts = fr.split(/\{(\w+)\}/);
      const source = parts.map((p, i) => (i % 2 ? `(?<${p}>.+?)` : p.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"))).join("");
      return { regex: new RegExp(`^${source}$`, "s"), en, fixed: parts.filter((_, i) => i % 2 === 0).join("").length };
    })
    .sort((a, b) => b.fixed - a.fixed);
  serverCache.clear();
  serverVersion += 1;
  listeners.forEach((fn) => fn());
}

export function hasServerMessages(): boolean {
  return serverPatterns.length > 0;
}

function serverPart(value: string, depth: number): string {
  const done = serverText(value, depth + 1);
  return done === value && value.includes(", ") ? value.split(", ").map((p) => serverText(p, depth + 1)).join(", ") : done;
}

/** Message du serveur (journal, alarme, erreur de sortie) dans la langue de l'interface ; inconnu : tel quel. */
export function serverText(text: string, depth = 0): string {
  if (current !== "en" || !text) return text;
  const exact = serverExact[text];
  if (exact !== undefined) return exact;
  if (depth > 3) return text;
  const cached = depth === 0 ? serverCache.get(text) : undefined;
  if (cached !== undefined) return cached;
  let out = text;
  for (const { regex, en } of serverPatterns) {
    const groups = regex.exec(text)?.groups;
    if (groups) {
      out = en.replace(/\{(\w+)\}/g, (m, k: string) => (k in groups ? serverPart(groups[k]!, depth) : m));
      break;
    }
  }
  if (depth === 0) {
    if (serverCache.size >= SERVER_CACHE_MAX) serverCache.clear();
    serverCache.set(text, out);
  }
  return out;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Locale des dates et des nombres : en-GB garde l'heure sur 24 h et le jour avant le mois. */
export function locale(): string {
  return current === "en" ? "en-GB" : "fr-FR";
}

type Vars = Record<string, string | number>;

function fill(text: string, vars?: Vars): string {
  return vars ? text.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m)) : text;
}

/** Texte traduit. */
export function t(fr: string, vars?: Vars): string {
  return fill(current === "en" ? EN[fr] ?? fr : fr, vars);
}

/** Même mot français, sens différents : tc("entrée", "Identifiant") se traduit par EN["entrée|Identifiant"]. */
export function tc(context: string, fr: string, vars?: Vars): string {
  return fill(current === "en" ? EN[`${context}|${fr}`] ?? fr : fr, vars);
}

/** Marque un texte à traduire sans le traduire (tables d'étiquettes) : affiché plus tard avec t(). */
export function N_(fr: string): string {
  return fr;
}

/** Texte traduit avec balises : rich("Cliquez <b>ici</b>", { b: (s) => <b>{s}</b> }). */
export function rich(fr: string, tags: Record<string, (children: string) => ReactNode>, vars?: Vars): ReactNode {
  const text = t(fr, vars);
  const parts: ReactNode[] = [];
  const re = /<(\w+)>(.*?)<\/\1>/g;
  let last = 0;
  for (let m = re.exec(text); m; m = re.exec(text)) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const render = tags[m[1]!];
    parts.push(createElement(Fragment, { key: m.index }, render ? render(m[2]!) : m[2]));
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return createElement(Fragment, null, ...parts);
}
